import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, LayoutDashboard } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Skeleton } from '@/components/ui/skeleton';
import { useBillingOverviewQuery } from '@/client/api/payment/payment.query';
import { BillingOverview, formatMoney } from '@/client/types/payment';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';

const RANGES = [7, 30, 90] as const;

const card = 'rounded-xl border border-gray-100 bg-white p-5 shadow-sm';

/** What is waiting on a person, in the order it should be worked. */
const ATTENTION: Array<{ key: keyof BillingOverview['attention']; label: string; to: string }> = [
  { key: 'overdue_invoices', label: 'Overdue invoices', to: '/admin/payments/invoices?status=overdue' },
  {
    key: 'past_due_subscriptions',
    label: 'Past-due subscriptions',
    to: '/admin/payments/subscriptions?billing_status=past_due',
  },
  { key: 'pending_refund_requests', label: 'Refund requests to review', to: '/admin/payments/refund-requests?status=pending' },
  { key: 'open_wallet_withdrawals', label: 'Wallet withdrawals in progress', to: '/admin/payments/payouts' },
  { key: 'open_store_payouts', label: 'Store payouts in progress', to: '/admin/payments/payouts?scope=store' },
  { key: 'pending_payments', label: 'Payments still pending', to: '/admin/payments?status=pending' },
];

const SUB_STATES: Array<[string, string]> = [
  ['active', 'Active'],
  ['trialing', 'Trialing'],
  ['payment_pending', 'Payment pending'],
  ['past_due', 'Past due'],
  ['paused', 'Paused'],
  ['canceled', 'Canceled'],
  ['expired', 'Expired'],
];

const Stat = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className={card}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className="mt-1 text-2xl font-black tabular-nums text-gray-900">{value}</p>
    {sub && <p className="mt-1 text-sm text-gray-500">{sub}</p>}
  </div>
);

/** One bar per day in the window, zero-filled so quiet days still show. */
const dailySeries = (data: BillingOverview, ccode: string) => {
  const byDate = new Map(
    data.series.filter((r) => r.ccode === ccode).map((r) => [r.date, r] as const),
  );
  const out: Array<{ date: string; label: string; collected: number; count: number }> = [];
  const day = new Date();
  day.setUTCHours(0, 0, 0, 0);
  day.setUTCDate(day.getUTCDate() - (data.days - 1));
  for (let i = 0; i < data.days; i += 1) {
    const iso = day.toISOString().slice(0, 10);
    const row = byDate.get(iso);
    out.push({
      date: iso,
      label: day.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }),
      collected: row?.collected ?? 0,
      count: row?.count ?? 0,
    });
    day.setUTCDate(day.getUTCDate() + 1);
  }
  return out;
};

const BillingOverviewPage = () => {
  useBreadcrumbs([{ label: 'Billing & Payments', href: '/admin/payments/overview' }, { label: 'Overview' }]);
  const [days, setDays] = useState<number>(30);
  const { data, isLoading, error } = useBillingOverviewQuery(days);

  // Headline currency = the one that collected most in the window.
  const main = useMemo(
    () => [...(data?.payments ?? [])].sort((a, b) => b.collected - a.collected)[0],
    [data],
  );
  const ccode = main?.ccode ?? 'GHS';
  const series = useMemo(() => (data ? dailySeries(data, ccode) : []), [data, ccode]);
  const outstanding = (data?.invoices ?? []).filter((i) => i.ccode === ccode);
  const owed = outstanding.reduce((s, i) => s + i.amount, 0);
  const owedCount = outstanding.reduce((s, i) => s + i.count, 0);
  const float = data?.wallets.find((w) => w.ccode === ccode);
  const active = data?.subscriptions.active;
  const otherCurrencies = (data?.payments ?? []).filter((p) => p.ccode !== ccode);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="mb-1 flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <LayoutDashboard className="h-4 w-4 text-white" />
              </div>
              <h1 className="text-2xl font-black text-gray-900">Billing &amp; Payments</h1>
            </div>
            <p className="text-sm text-gray-500">
              Money in, money owed, and what needs someone&apos;s attention. Follows the Live / Test
              switch, except wallets, payouts and refund requests, which are always platform-wide.
            </p>
          </div>
          <div className="flex gap-1 rounded-lg border border-gray-200 bg-white p-1" role="group" aria-label="Period">
            {RANGES.map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={days === d}
                onClick={() => setDays(d)}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  days === d ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {d} days
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
            The overview could not be loaded. {(error as Error).message}
          </div>
        )}

        {isLoading && !data && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-28 w-full" />
            ))}
          </div>
        )}

        {data && (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label={`Collected, last ${data.days} days`}
                value={formatMoney(main?.collected ?? 0, ccode)}
                sub={`${(main?.collected_count ?? 0).toLocaleString()} payments`}
              />
              <Stat
                label={`Refunded, last ${data.days} days`}
                value={formatMoney(main?.refunded ?? 0, ccode)}
                sub={`${(main?.refunded_count ?? 0).toLocaleString()} refunds`}
              />
              <Stat
                label="Invoices outstanding"
                value={formatMoney(owed, ccode)}
                sub={`${owedCount.toLocaleString()} unpaid`}
              />
              <Stat
                label="Held in wallets"
                value={formatMoney(float?.balance ?? 0, ccode)}
                sub={`${(float?.wallets ?? 0).toLocaleString()} wallets · ${(float?.frozen ?? 0).toLocaleString()} frozen`}
              />
            </div>

            {otherCurrencies.length > 0 && (
              <p className="text-sm text-gray-500">
                Also collected:{' '}
                {otherCurrencies.map((p) => formatMoney(p.collected, p.ccode)).join(', ')}.
              </p>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className={`${card} lg:col-span-2`}>
                <h2 className="text-sm font-semibold text-gray-800">
                  Collected per day ({ccode})
                </h2>
                <div className="mt-4 h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={series} margin={{ left: 4, right: 12 }} barCategoryGap={2}>
                      <CartesianGrid vertical={false} stroke="#f3f4f6" />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={{ stroke: '#e5e7eb' }}
                        minTickGap={24}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#6b7280' }}
                        tickLine={false}
                        axisLine={false}
                        width={56}
                        tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(v))}
                      />
                      <Tooltip
                        cursor={{ fill: '#f3f4f6' }}
                        formatter={(v: number, _n, item) => [
                          `${formatMoney(v, ccode)} · ${item?.payload?.count ?? 0} payments`,
                          'Collected',
                        ]}
                      />
                      <Bar dataKey="collected" fill="#2563eb" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className={card}>
                <h2 className="text-sm font-semibold text-gray-800">Needs attention</h2>
                <ul className="mt-2 divide-y divide-gray-50">
                  {ATTENTION.map(({ key, label, to }) => {
                    const n = data.attention[key];
                    return (
                      <li key={key}>
                        <Link
                          to={to}
                          className="flex items-center justify-between gap-3 py-2.5 text-sm hover:text-blue-600"
                        >
                          <span className={n ? 'text-gray-900' : 'text-gray-400'}>{label}</span>
                          <span className="flex items-center gap-1">
                            <span
                              className={`tabular-nums ${n ? 'font-semibold text-gray-900' : 'text-gray-400'}`}
                            >
                              {n.toLocaleString()}
                            </span>
                            <ChevronRight className="h-4 w-4 text-gray-300" />
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <div className={card}>
                <div className="flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold text-gray-800">Subscriptions</h2>
                  <Link to="/admin/payments/subscriptions" className="text-sm text-blue-600 hover:underline">
                    View all
                  </Link>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  {(active?.count ?? 0).toLocaleString()} active, billing{' '}
                  {formatMoney(active?.amount ?? 0, ccode)} per cycle across all intervals.
                </p>
                <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4">
                  {SUB_STATES.map(([state, label]) => (
                    <div key={state}>
                      <dt className="text-xs text-gray-500">{label}</dt>
                      <dd className="font-semibold tabular-nums text-gray-900">
                        {(data.subscriptions[state]?.count ?? 0).toLocaleString()}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div className={card}>
                <div className="flex items-baseline justify-between">
                  <h2 className="text-sm font-semibold text-gray-800">Payouts in progress</h2>
                  <Link to="/admin/payments/payouts?scope=all" className="text-sm text-blue-600 hover:underline">
                    View all
                  </Link>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                  {(['wallet', 'store'] as const).map((k) => (
                    <div key={k}>
                      <dt className="text-xs text-gray-500">
                        {k === 'wallet' ? 'Wallet withdrawals' : 'Store payouts'}
                      </dt>
                      <dd className="font-semibold tabular-nums text-gray-900">
                        {formatMoney(data.payouts[k]?.amount ?? 0, ccode)}
                      </dd>
                      <dd className="text-gray-500">
                        {(data.payouts[k]?.count ?? 0).toLocaleString()} open
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default BillingOverviewPage;
